// Contrast math for accent colors (WCAG 2.x relative luminance).
//
// Accents are used as text and borders on dark cards, and as button fills
// with dark text on top, so an accent must pass AA (4.5:1) in both directions.

export const SURFACE = '#111827'
export const PAGE_BG = '#0a0e1a'
export const INK_DARK = '#0a0e1a'
export const INK_LIGHT = '#ffffff'
export const DEFAULT_ACCENT = '#f2800d'
export const MIN_CONTRAST = 4.5

// Quick picks for team and event accents; every one passes the rule below.
export const ACCENT_PRESETS = [
  '#f2800d', '#fbbf24', '#a3e635', '#34d399', '#2dd4bf', '#38bdf8',
  '#60a5fa', '#a78bfa', '#c084fc', '#f472b6', '#fb7185', '#f87171',
]

const HEX = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i

export function normalizeHex(input) {
  if (typeof input !== 'string') return null
  const match = HEX.exec(input.trim())
  if (!match) return null
  let hex = match[1].toLowerCase()
  if (hex.length === 3) hex = [...hex].map((c) => c + c).join('')
  return `#${hex}`
}

export function parseHex(input) {
  const hex = normalizeHex(input)
  if (!hex) return null
  return {
    r: parseInt(hex.slice(1, 3), 16),
    g: parseInt(hex.slice(3, 5), 16),
    b: parseInt(hex.slice(5, 7), 16),
  }
}

function channel(value) {
  const c = value / 255
  return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4
}

export function relativeLuminance(input) {
  const rgb = parseHex(input)
  if (!rgb) throw new TypeError(`Not a hex color: ${input}`)
  return 0.2126 * channel(rgb.r) + 0.7152 * channel(rgb.g) + 0.0722 * channel(rgb.b)
}

export function contrastRatio(a, b) {
  const la = relativeLuminance(a)
  const lb = relativeLuminance(b)
  const [hi, lo] = la > lb ? [la, lb] : [lb, la]
  return (hi + 0.05) / (lo + 0.05)
}

// Text color to put on a solid accent fill.
export function accentInk(accent) {
  return contrastRatio(accent, INK_DARK) >= contrastRatio(accent, INK_LIGHT) ? INK_DARK : INK_LIGHT
}

export function validateAccent(input) {
  const value = normalizeHex(input)
  if (!value) return { ok: false, value: null, reason: 'Use a hex color like #f2800d.' }
  const onSurface = contrastRatio(value, SURFACE)
  const ink = accentInk(value)
  const inkContrast = contrastRatio(value, ink)
  if (onSurface < MIN_CONTRAST) {
    return {
      ok: false,
      value,
      onSurface,
      reason: `Too dark to read on the site's dark cards (${onSurface.toFixed(2)}:1, needs ${MIN_CONTRAST}:1). Pick a lighter shade.`,
    }
  }
  if (inkContrast < MIN_CONTRAST) {
    return {
      ok: false,
      value,
      onSurface,
      reason: `Text on this color would fail contrast (${inkContrast.toFixed(2)}:1). Pick a lighter or darker shade.`,
    }
  }
  return { ok: true, value, onSurface, ink }
}

// Falls back to the default accent when a stored color is missing or no longer passes.
export function safeAccent(input) {
  const result = validateAccent(input)
  return result.ok ? result.value : DEFAULT_ACCENT
}
